import * as dt from '@cnapse/shared/data.types.js';

export interface WaferImagingTemplateListItem {
  id: number;
  name: string;
  waferSize: string;
  idSide: string;
  markOffsetDeg: number;
  isSystemTemplate: boolean;
  startingLightingModes?: Array<{ mode: number; startPower: number; ledMask?: number | null }>;
}

export interface ConfiguredWaferCameraRole {
  supportedWaferSize: string;
  idSide: string;
}

/** Returns how many gateways have a bound wafer camera matching the template geometry. */
export function countGatewaysMatchingTemplate(
  template: Pick<WaferImagingTemplateListItem, 'waferSize' | 'idSide'>,
  gatewayCameraRoles: ConfiguredWaferCameraRole[][],
): number {
  return gatewayCameraRoles.filter((cameras) =>
    cameras.some(
      (camera) =>
        camera.supportedWaferSize === template.waferSize && camera.idSide === template.idSide,
    ),
  ).length;
}

function parseWaferMm(size: string): number {
  const match = /^(\d+)mm$/.exec(size.trim());
  return match ? Number(match[1]) : NaN;
}

/** True when template wafer diameter fits the physical load port (e.g. 150mm wafer on 200mm port). */
export function waferSizeFitsLoadPort(waferSize: string, loadPortSize: string): boolean {
  const waferMm = parseWaferMm(waferSize);
  const portMm = parseWaferMm(loadPortSize);
  if (Number.isNaN(waferMm) || Number.isNaN(portMm)) {
    return true;
  }
  return waferMm <= portMm;
}

/**
 * Templates usable for a container slot scan: must match a configured gateway camera,
 * bound container size (when known), and load port capacity.
 */
export function filterTemplatesForSlotScan(
  templates: WaferImagingTemplateListItem[],
  options: {
    configuredCameras: ConfiguredWaferCameraRole[];
    containerSize: string | null;
    loadPortSize: string | null;
  },
): WaferImagingTemplateListItem[] {
  const { configuredCameras, containerSize, loadPortSize } = options;

  return templates.filter((template) => {
    const cameraMatch = configuredCameras.some(
      (camera) =>
        camera.supportedWaferSize === template.waferSize && camera.idSide === template.idSide,
    );
    if (!cameraMatch) {
      return false;
    }

    if (loadPortSize && !waferSizeFitsLoadPort(template.waferSize, loadPortSize)) {
      return false;
    }

    if (containerSize) {
      return dt.sizeMatch(
        template.waferSize as dt.WAFER_SIZE,
        containerSize as dt.CONTAINER_SIZE,
      );
    }

    return true;
  });
}
